import type { ResearchBranchId } from './types'
import { RESEARCH_BASE_MAX_RANK, RESEARCH_MAX_RANK } from './types'

/**
 * Deterministic Research cost curves (Ingots).
 *
 * BASE (preserved — early campaign accessible):
 * POWER / TEMPO (5 ranks): 10, 20, 35, 55, 80  → sum 200
 * RANGE (3 ranks):         15, 30, 55         → sum 100
 * One tower base max = 500. All six = 3000.
 *
 * ADVANCED (ranks beyond base, unlock via Chapter 2):
 * Steep long-term sink — Chapters 1+2 mastery must NOT exhaust the tree.
 * POWER / TEMPO 6–8: 250, 400, 600 → +1250
 * RANGE 4–5:         180, 300     → +480
 * One tower advanced = +2980. All six advanced = +17880.
 * Grand total max = 20880.
 *
 * Effects unchanged: still +5%/rank damage (FIRE), +4% range, +4% tempo rate.
 * Full Advanced ≈ +40% dmg / +20% range / +32% tempo — no Tier III inflation.
 */

export const POWER_TEMPO_COSTS = [10, 20, 35, 55, 80, 250, 400, 600] as const
export const RANGE_COSTS = [15, 30, 55, 180, 300] as const

/** Cost to purchase the next rank (currentRank → currentRank+1). currentRank is 0-based completed. */
export function researchCostForNextRank(
  branch: ResearchBranchId,
  currentRank: number
): number | null {
  if (!Number.isFinite(currentRank) || currentRank < 0) return null
  const costs = branch === 'range' ? RANGE_COSTS : POWER_TEMPO_COSTS
  const max = RESEARCH_MAX_RANK[branch]
  if (currentRank >= max || currentRank >= costs.length) return null
  return costs[currentRank]!
}

export function costToMaxBranch(branch: ResearchBranchId, fromRank = 0): number {
  const costs = branch === 'range' ? RANGE_COSTS : POWER_TEMPO_COSTS
  const max = RESEARCH_MAX_RANK[branch]
  let total = 0
  for (let r = Math.max(0, fromRank); r < max && r < costs.length; r += 1) {
    total += costs[r]!
  }
  return total
}

export function costToMaxTower(fromRanks?: {
  power?: number
  range?: number
  tempo?: number
}): number {
  return (
    costToMaxBranch('power', fromRanks?.power ?? 0) +
    costToMaxBranch('range', fromRanks?.range ?? 0) +
    costToMaxBranch('tempo', fromRanks?.tempo ?? 0)
  )
}

/** Base-tier max (pre-Advanced) — preserved for economy docs / profiles. */
export function costToBaseMaxTower(fromRanks?: {
  power?: number
  range?: number
  tempo?: number
}): number {
  const from = {
    power: fromRanks?.power ?? 0,
    range: fromRanks?.range ?? 0,
    tempo: fromRanks?.tempo ?? 0
  }
  let total = 0
  for (const branch of ['power', 'range', 'tempo'] as const) {
    const costs = branch === 'range' ? RANGE_COSTS : POWER_TEMPO_COSTS
    const baseMax = RESEARCH_BASE_MAX_RANK[branch]
    for (let r = Math.max(0, from[branch]); r < baseMax && r < costs.length; r += 1) {
      total += costs[r]!
    }
  }
  return total
}

export const COST_ONE_TOWER_BASE_MAX = costToBaseMaxTower()
export const COST_ALL_TOWERS_BASE_MAX = COST_ONE_TOWER_BASE_MAX * 6
export const COST_ONE_TOWER_MAX = costToMaxTower()
export const COST_ALL_TOWERS_MAX = COST_ONE_TOWER_MAX * 6
export const COST_ALL_TOWERS_ADVANCED_ONLY = COST_ALL_TOWERS_MAX - COST_ALL_TOWERS_BASE_MAX

export function isAdvancedRank(branch: ResearchBranchId, rank: number): boolean {
  return rank > RESEARCH_BASE_MAX_RANK[branch]
}
